import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

const readCookie = (name) => document.cookie
  .split('; ')
  .find((part) => part.startsWith(`${name}=`))
  ?.slice(name.length + 1);

// Cookie-authenticated mutations use a JWT-bound double-submit CSRF token.
api.interceptors.request.use((config) => {
  const method = (config.method || 'get').toLowerCase();
  if (!['get', 'head', 'options'].includes(method)) {
    const csrfToken = readCookie('pw_csrf');
    if (csrfToken) config.headers['X-CSRF-Token'] = decodeURIComponent(csrfToken);
  }
  return config;
});

// Handle 401 responses - only clear token on auth endpoints
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      const url = error.config?.url || '';
      // Only auto-logout if the /auth/me check itself fails with 401
      // This means the token is truly invalid/expired
      if (url.includes('/api/auth/me')) {
        window.dispatchEvent(new Event('auth-logout'));
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/api/auth/register', data),
  login: (data) => api.post('/api/auth/login', data),
  logout: () => api.post('/api/auth/logout'),
  requestPasswordReset: (email) => api.post('/api/auth/password-reset/request', { email }),
  confirmPasswordReset: (token, password) => api.post('/api/auth/password-reset/confirm', { token, password }),
  getMe: () => api.get('/api/auth/me'),
  updateProfile: (data) => api.put('/api/auth/profile', data),
  addAddress: (data) => api.post('/api/auth/addresses', data),
  deleteAddress: (id) => api.delete(`/api/auth/addresses/${id}`),
};

// Catalog
export const catalogAPI = {
  getCategories: () => api.get('/api/categories'),
  getCategory: (slug) => api.get(`/api/categories/${slug}`),
  getHolidays: () => api.get('/api/holidays'),
  getProducts: (params) => api.get('/api/products', { params }),
  getProduct: (slug) => api.get(`/api/products/${slug}`),
  searchSuggestions: (q) => api.get('/api/products/search-suggestions', { params: { q } }),
};

// Cart
export const cartAPI = {
  getCart: () => api.get('/api/cart'),
  addToCart: (data) => api.post('/api/cart/add', data),
  updateItem: (productId, data) => api.put(`/api/cart/item/${productId}`, data),
  removeItem: (productId) => api.delete(`/api/cart/item/${productId}`),
  applyCoupon: (code) => api.post('/api/cart/coupon', { code }),
  removeCoupon: () => api.delete('/api/cart/coupon'),
  clearCart: () => api.delete('/api/cart'),
};

// Orders
export const orderAPI = {
  createOrder: (data) => api.post('/api/orders', data),
  quoteCart: (data) => api.post('/api/cart/quote', data),
  paymentMethods: () => api.get('/api/payments/methods'),
  getOrders: (params) => api.get('/api/orders', { params }),
  getOrder: (id) => api.get(`/api/orders/${id}`),
  resumeStripeCheckout: (id) => api.get(`/api/orders/${id}/stripe-checkout`),
  reorder: (id) => api.post(`/api/orders/${id}/reorder`),
};

// Wishlist
export const wishlistAPI = {
  getWishlist: () => api.get('/api/wishlist'),
  addToWishlist: (productId) => api.post('/api/wishlist', { product_id: productId }),
  removeFromWishlist: (productId) => api.delete(`/api/wishlist/${productId}`),
};

// Weekly Specials & Prebook
export const specialsAPI = {
  getWeeklySpecials: (params) => api.get('/api/weekly-specials', { params }),
  getPrebookInfo: () => api.get('/api/prebook'),
  getSpecialsCatalog: (params) => api.get('/api/specials-catalog', { params }),
  getSpecialsCatalogItem: (id) => api.get(`/api/specials-catalog/${id}`),
};

// Newsletter & Contact
export const miscAPI = {
  subscribeNewsletter: (email) => api.post('/api/newsletter', { email }),
  submitContact: (data) => api.post('/api/contact', data),
  getVolumeRules: () => api.get('/api/volume-rules'),
  getColors: () => api.get('/api/colors'),
};

// Site Settings (CMS)
let siteSettingsRequest = null;
let siteSettingsCachedAt = 0;
const SITE_SETTINGS_CACHE_MS = 30_000;

const getSiteSettings = () => {
  if (!siteSettingsRequest || Date.now() - siteSettingsCachedAt >= SITE_SETTINGS_CACHE_MS) {
    siteSettingsCachedAt = Date.now();
    siteSettingsRequest = api.get('/api/site-settings').catch((error) => {
      siteSettingsRequest = null;
      siteSettingsCachedAt = 0;
      throw error;
    });
  }
  return siteSettingsRequest;
};

const updateSiteSettings = async (data) => {
  const response = await api.put('/api/admin/site-settings', data);
  siteSettingsRequest = null;
  siteSettingsCachedAt = 0;
  return response;
};

export const siteAPI = {
  getSettings: getSiteSettings,
};

// Admin
export const adminAPI = {
  getStats: () => api.get('/api/admin/stats'),
  getConfigurationStatus: () => api.get('/api/admin/configuration-status'),
  createFullBackup: () => api.post('/api/admin/backup/jobs'),
  getFullBackupStatus: (id) => api.get(`/api/admin/backup/jobs/${id}`),
  downloadFullBackup: (id) => api.get(`/api/admin/backup/jobs/${id}/download`, { responseType: 'blob' }),
  getSiteSettings,
  updateSiteSettings,
  getProducts: (params) => api.get('/api/admin/products', { params }),
  createProduct: (data) => api.post('/api/admin/products', data),
  updateProduct: (id, data) => api.put(`/api/admin/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/api/admin/products/${id}`),
  createCategory: (data) => api.post('/api/admin/categories', data),
  updateCategory: (id, data) => api.put(`/api/admin/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/api/admin/categories/${id}`),
  getOrders: (params) => api.get('/api/admin/orders', { params }),
  updateOrderStatus: (id, status) => api.put(`/api/admin/orders/${id}/status`, { status }),
  exportOrders: (params) => api.get('/api/admin/orders/export', { params, responseType: 'blob' }),
  getCustomers: (params) => api.get('/api/admin/customers', { params }),
  updateCustomer: (id, data) => api.put(`/api/admin/customers/${id}`, data),
  getVolumeRules: () => api.get('/api/admin/volume-rules'),
  createVolumeRule: (data) => api.post('/api/admin/volume-rules', data),
  updateVolumeRule: (id, data) => api.put(`/api/admin/volume-rules/${id}`, data),
  deleteVolumeRule: (id) => api.delete(`/api/admin/volume-rules/${id}`),
  getCoupons: () => api.get('/api/admin/coupons'),
  createCoupon: (data) => api.post('/api/admin/coupons', data),
  deleteCoupon: (id) => api.delete(`/api/admin/coupons/${id}`),
  toggleWeeklySpecial: (id, val) => api.put(`/api/admin/products/${id}/weekly-special`, { is_weekly_special: val }),
  togglePrebook: (id, val) => api.put(`/api/admin/products/${id}/prebook`, { is_prebook_eligible: val }),
  approveCustomer: (id, approved) => api.put(`/api/admin/customers/${id}/approve`, { approved }),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/admin/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  uploadMultipleImages: (files) => {
    const formData = new FormData();
    files.forEach((file, i) => formData.append(`file_${i}`, file));
    return api.post('/api/admin/upload-multiple', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  uploadSpecialsExcel: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/admin/specials-catalog/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getSpecialsCatalogStats: () => api.get('/api/admin/specials-catalog/stats'),
  getSpecialsCatalogItems: (params) => api.get('/api/specials-catalog', { params }),
  createSpecialsItem: (data) => api.post('/api/admin/specials-catalog', data),
  updateSpecialsItem: (id, data) => api.put(`/api/admin/specials-catalog/${id}`, data),
  deleteSpecialsItem: (id) => api.delete(`/api/admin/specials-catalog/${id}`),
  // Holidays
  getHolidays: () => api.get('/api/admin/holidays'),
  createHoliday: (data) => api.post('/api/admin/holidays', data),
  updateHoliday: (id, data) => api.put(`/api/admin/holidays/${id}`, data),
  deleteHoliday: (id) => api.delete(`/api/admin/holidays/${id}`),
  // Inventory import/export
  downloadInventoryTemplate: () => api.get('/api/admin/inventory/template', { responseType: 'blob' }),
  importInventory: (formData) => api.post('/api/admin/inventory/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Subscribers
  getSubscribers: (params) => api.get('/api/admin/subscribers', { params }),
  deleteSubscriber: (id) => api.delete(`/api/admin/subscribers/${id}`),
  exportSubscribers: () => api.get('/api/admin/subscribers/export', { responseType: 'blob' }),
  importSubscribers: (formData) => api.post('/api/admin/subscribers/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Colors
  addColor: (name) => api.post('/api/admin/colors', { name }),
  deleteColor: (name) => api.delete(`/api/admin/colors/${encodeURIComponent(name)}`),
  // Users
  createUser: (data) => api.post('/api/admin/users', data),
  updateUser: (id, data) => api.put(`/api/admin/users/${id}`, data),
  toggleRole: (id, role) => api.put(`/api/admin/users/${id}/role`, { role }),
  deleteUser: (id) => api.delete(`/api/admin/users/${id}`),
  assignAdminRole: (id, roleId) => api.put(`/api/admin/users/${id}/admin-role`, { role_id: roleId }),
  setUserActive: (id, active) => api.put(`/api/admin/users/${id}/active`, { active }),
  getPermissionCatalog: () => api.get('/api/admin/permissions/catalog'),
  getRoles: () => api.get('/api/admin/roles'),
  createRole: (data) => api.post('/api/admin/roles', data),
  updateRole: (id, data) => api.put(`/api/admin/roles/${id}`, data),
  deleteRole: (id, replacementRoleId) => api.delete(`/api/admin/roles/${id}`, { params: { replacement_role_id: replacementRoleId } }),
  getRoleAudit: (params) => api.get('/api/admin/audit/roles', { params }),
  toggleVisibility: (id, isActive) => api.put(`/api/admin/products/${id}/visibility`, { is_active: isActive }),
};

export default api;
